import type {CustomReviewColumnTypes} from "./ColumnTypes";
import {toMuiXBasicColumn} from "../../../../../../components/muix-data-grid/toMuiXBasicColumn.tsx";
import {CategoryPreviousCell} from "../cells/CategoryPreviousCell.tsx";
import {CategoryCell} from "../cells/CategoryCell.tsx";
import {ReasoningCell} from "../cells/ReasoningCell.tsx";
import {StateCell} from "../cells/StateCell.tsx";
import {ScoreCell} from "../cells/ScoreCell.tsx";
import type {
    JSONSerializableColumnType
} from "../../../../../../components/muix-data-grid/cells/MuiXBasicColumnTypes.ts";
import {isCustomType} from "../../../../../../components/muix-data-grid/cells/MuiXBasicColumnTypes.ts";
import type {
    DeserializedGridColDef,
    JSONSerializableGridColDef
} from "../../../../../../components/muix-data-grid/MuiXColumnDefinitions.ts";
import type {GridRowParams, ValueOptions} from "@mui/x-data-grid-premium";
import {
    getGridStringOperators,
    GRID_CHECKBOX_SELECTION_COL_DEF,
    GRID_CHECKBOX_SELECTION_FIELD
} from "@mui/x-data-grid-premium";
import {RootSelectionBox} from "../../selection/RootSelectionBox.tsx";
import {GRID_REVIEW_ACTION_FIELD, ReviewActionsCell} from "../cells/ReviewActionsCell.tsx";
import React from "react";
import {scoreBetweenFilter} from "../filters/ScoreBetweenFilter.tsx";
import {dateBetweenFilter} from "../filters/DateBetweenFilter.tsx";
import {
    MatPartReviewRowStateToString,
    VALID_MAT_PART_REVIEW_ROW_STATES,
} from "../../../../../../services/classes/MaterializedClasses.ts";
import type {PartGridRowModel} from "../CategorizationTableDataModel.ts";
import {GROUP_KEY, GROUP_VAL} from "../CategorizationTableDataModel.ts";
import {GroupingCell} from "../cells/GroupingCell.tsx";
import {BusinessUnitCell} from "../cells/BusinessUnitCell.tsx";
import {BUSINESS_UNIT_VALUE_FIELD} from "../../../../controllers/CategorizationReviewPageFilterController.ts";
import type {Optional} from "../../../../../../utils/ts-utils.ts";
import type {ApiCategory} from "../../../../../../services/classes/Category.ts";
import {categoryExportString,} from "../../../../../../services/classes/Category.ts";
import {DownloadFileCell} from "../cells/DowloadFileCell.tsx";

export function toReviewMuiXColumn<T extends CustomReviewColumnTypes>(serializedSpec: JSONSerializableGridColDef<T>, isMithraStaffGroup: boolean): DeserializedGridColDef<T> {
    const spec = toMuiXBasicColumn(serializedSpec, isMithraStaffGroup);
    const serializableType: JSONSerializableColumnType<CustomReviewColumnTypes> = serializedSpec.serializableType;

    if (spec.field === GRID_CHECKBOX_SELECTION_FIELD) {
        return {
            ...GRID_CHECKBOX_SELECTION_COL_DEF,
            ...spec,
            // renderCell: (params) => <SingleSelectionCell {...params}/>,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            renderHeader: (_params) => <RootSelectionBox/>,
        }
    }
    if (spec.field === GRID_REVIEW_ACTION_FIELD) {
        return {
            ...spec,
            renderHeader: () => 'Actions',
            getActions: (params: GridRowParams<PartGridRowModel>) => [<ReviewActionsCell key="" {...params}/>],
        } as DeserializedGridColDef<T>
    }
    if (spec.field === GROUP_VAL) {
        spec.filterOperators = getGridStringOperators().filter(o => o.value === 'contains');
    }
    if (spec.field === GROUP_KEY) {
        spec.renderCell = params => <GroupingCell {...params}/>;
    }
    if (spec.field === BUSINESS_UNIT_VALUE_FIELD) {
        spec.renderCell = params => <BusinessUnitCell {...params}/>;
    }
    if (spec.field === 'state') {
        spec.renderCell = params => <StateCell {...params}/>;
    }

    if (!isCustomType(serializableType)) {
        return spec
    }

    // Make sure all cases of ReviewColumnTypes are handled!
    switch (serializableType) {
        case 'custom-previous-cat':
            return {
                // TODO: Add filters on previous category
                ...spec,
                filterable: false, // TODO: Filter component for categories is not yet created
                renderCell: CategoryPreviousCell,
                renderHeaderFilter: () => null,
                valueFormatter: (value: Optional<ApiCategory, 'id'>) => categoryExportString(value),
            }
        case 'custom-cat':
            return {
                ...spec,
                type: 'custom',
                renderCell: CategoryCell,
                renderHeaderFilter: () => null, // Filter happens by bar-charts only at the moment
                valueFormatter: (value: Optional<ApiCategory, 'id'>) => categoryExportString(value),
            }
        case 'custom-reasoning':
            return {
                ...spec,
                renderCell: ReasoningCell,
                headerName: 'Reason',
                renderHeaderFilter: () => null,
            }
        case 'custom-status':
            return {
                ...spec,
                type: 'singleSelect',
                valueOptions: VALID_MAT_PART_REVIEW_ROW_STATES
                    .map<ValueOptions>((value) => ({value, label: MatPartReviewRowStateToString(value)})),
                headerName: 'Status',
                renderHeaderFilter: () => null, // The header filter does not work with multi select, it throws an Error
            };
        case 'custom-score':
            return {
                ...spec,
                type: 'number',
                renderCell: ScoreCell,
                filterOperators: [scoreBetweenFilter],
            };
        case 'custom-date':
            return {
                ...spec,
                type: 'date',
                filterOperators: [dateBetweenFilter],
            };
        case 'custom-download-file':
            return {
                ...spec,
                type: 'custom',
                renderCell: DownloadFileCell,
                renderHeaderFilter: () => null,
                filterable: false,
                sortable: false,
                align: 'center',
                width: 60,
                resizable: false,
            };
        default: {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const _never = serializableType;
            return spec;
        }
    }
}
